export const FEEDBACK_SENTIMENTS = ['positive', 'negative'] as const;
export type FeedbackSentiment = typeof FEEDBACK_SENTIMENTS[number];

export const FEEDBACK_REASONS = [
  'clear_rules',
  'unclear_rules',
  'inventory_well_stocked',
  'lack_of_inventory',
  'great_rewards',
  'min_threshold_too_high',
  'feels_motivating',
  'payout_took_too_long',
  'good_rep',
  'engaging',
  'good_timing',
  'relevant_to_customers',
  'bad_rep',
  'not_engaging',
  'bad_timing',
  'weak_incentive',
  'something_else',
] as const;
export type FeedbackReason = typeof FEEDBACK_REASONS[number];

export const FEEDBACK_DOCUMENT_TYPES = ['spark', 'event'] as const;
export type FeedbackDocumentType = typeof FEEDBACK_DOCUMENT_TYPES[number];

export const MODERATION_STATUSES = ['pending', 'approved', 'rejected', 'flagged'] as const;
export type ModerationStatus = typeof MODERATION_STATUSES[number];

export interface Feedback {
  _id: string;
  documentType: FeedbackDocumentType;
  documentId: string;
  userId: string;
  sentiment: FeedbackSentiment;
  reasons?: FeedbackReason[];
  comment?: string;
  moderationStatus: ModerationStatus;
  createdAt: Date;
  updatedAt?: Date;
  deletedAt?: Date | null;
}

export interface CreateFeedbackResponseBody {
  data: Feedback;
}

export interface GetFeedbackByDocumentResponseBody {
  data: Feedback | null;
}

/**
 * Feedback with enriched user data including location information
 *
 * @property user.preferredLocationId - User's explicit preferred location (from AccountMembership)
 * @property user.lastTransactionLocationId - Most recent transaction location (from PosEmployeeProfile)
 * @property user.locationIds - All locations user has access to (fallback)
 */
export interface FeedbackWithUser extends Feedback {
  user?: {
    _id: string;
    firstName: string;
    lastName: string;
    fullName: string;
    locationIds: string[];
    locationNames: string[];
    avatarUrl?: string;
    preferredLocationId?: string;
    lastTransactionLocationId?: string;
  };
}

export interface GetAdminFeedbackResponseBody {
  data: FeedbackWithUser[];
  total: number;
}

export interface UIReasonOption {
  label: string;
  value: FeedbackReason;
  sentiment: FeedbackSentiment;
}

export const REASON_MAP: Record<FeedbackReason, UIReasonOption> = {
  clear_rules: {
    label: '🎯 Clear rules',
    value: 'clear_rules',
    sentiment: 'positive',
  },
  unclear_rules: {
    label: '❓ Unclear rules',
    value: 'unclear_rules',
    sentiment: 'negative',
  },
  inventory_well_stocked: {
    label: '🛒 Inventory well stocked',
    value: 'inventory_well_stocked',
    sentiment: 'positive',
  },
  great_rewards: {
    label: '💰 Great rewards',
    value: 'great_rewards',
    sentiment: 'positive',
  },
  min_threshold_too_high: {
    label: '🚧 Threshold too high',
    value: 'min_threshold_too_high',
    sentiment: 'negative',
  },
  feels_motivating: {
    label: '🏆 Feels motivating',
    value: 'feels_motivating',
    sentiment: 'positive',
  },
  payout_took_too_long: {
    label: '⏰ Slow payout',
    value: 'payout_took_too_long',
    sentiment: 'negative',
  },
  good_rep: {
    label: '🤝 Helpful brand rep',
    value: 'good_rep',
    sentiment: 'positive',
  },
  engaging: {
    label: '🎉 Fun & engaging',
    value: 'engaging',
    sentiment: 'positive',
  },
  good_timing: {
    label: '🕒 Good timing',
    value: 'good_timing',
    sentiment: 'positive',
  },
  relevant_to_customers: {
    label: '🎯 Relevant to customers',
    value: 'relevant_to_customers',
    sentiment: 'positive',
  },
  bad_rep: {
    label: '🙅 Brand rep unhelpful',
    value: 'bad_rep',
    sentiment: 'negative',
  },
  not_engaging: {
    label: '💤 Not engaging',
    value: 'not_engaging',
    sentiment: 'negative',
  },
  bad_timing: {
    label: '🕒 Bad timing',
    value: 'bad_timing',
    sentiment: 'negative',
  },
  lack_of_inventory: {
    label: '📦 Low inventory',
    value: 'lack_of_inventory',
    sentiment: 'negative',
  },
  weak_incentive: {
    label: '💸 Weak incentive',
    value: 'weak_incentive',
    sentiment: 'negative',
  },
  something_else: {
    label: '💭 Something else',
    value: 'something_else',
    sentiment: 'negative',
  },
} as const;

export const ORDERED_REASON_MAP: Record<FeedbackDocumentType, readonly UIReasonOption[]> = {
  spark: [
    // positive
    REASON_MAP.clear_rules,
    REASON_MAP.inventory_well_stocked,
    REASON_MAP.great_rewards,
    REASON_MAP.feels_motivating,
    // negative
    REASON_MAP.min_threshold_too_high,
    REASON_MAP.lack_of_inventory,
    REASON_MAP.weak_incentive,
    REASON_MAP.something_else,
  ],
  event: [
    // positive
    REASON_MAP.good_rep,
    REASON_MAP.engaging,
    REASON_MAP.good_timing,
    REASON_MAP.relevant_to_customers,
    // negative
    REASON_MAP.bad_rep,
    REASON_MAP.bad_timing,
    REASON_MAP.not_engaging,
    REASON_MAP.lack_of_inventory,
  ],
} as const;
